import { useState, type ReactNode } from "react";

import { useT, type MessageKey } from "../../shared/i18n";
import {
  ActionBar,
  Badge,
  Banner,
  Button,
  Card,
  GroupLabel,
  Icons,
  type BadgeTone,
} from "../../shared/ui";

interface ProposalSummary {
  id: string;
  status: string;
  changes: readonly string[];
}

const STATUS: Partial<Record<string, { tone: BadgeTone; key: MessageKey }>> = {
  pending: { tone: "info", key: "proposal.status.pending" },
  applied: { tone: "success", key: "proposal.status.applied" },
  rejected: { tone: "neutral", key: "proposal.status.rejected" },
  // "Needs attention, but not an error" — a stale proposal is regenerated
  // through the Assistant rather than retried here.
  stale: { tone: "neutral", key: "proposal.status.stale" },
  expired: { tone: "warning", key: "proposal.status.expired" },
};

const OPERATION_SEPARATOR = ": ";

/** A change is `operation` or `operation: record id`. */
function parseChange(change: string): { operation: string; recordId?: string } {
  const at = change.indexOf(OPERATION_SEPARATOR);
  if (at < 0) return { operation: change };
  return {
    operation: change.slice(0, at),
    recordId: change.slice(at + OPERATION_SEPARATOR.length),
  };
}

function operationTile(operation: string): {
  className: string;
  icon: ReactNode;
} {
  switch (operation.trim().toLowerCase()) {
    case "create":
    case "add":
      return {
        className: "bg-success-bg text-success",
        icon: <Icons.Plus size={18} />,
      };
    case "update":
    case "edit":
      return {
        className: "bg-accent-subtle text-accent-fg",
        icon: <Icons.Edit size={18} />,
      };
    case "delete":
    case "remove":
      return {
        className: "bg-error-bg text-error",
        icon: <Icons.Trash size={18} />,
      };
    default:
      return {
        className: "bg-neutral-bg text-neutral",
        icon: <span className="size-2 rounded-full bg-current" />,
      };
  }
}

function ChangeRow({ change }: { change: string }) {
  const { operation, recordId } = parseChange(change);
  const tile = operationTile(operation);
  return (
    <div
      data-row=""
      className="relative flex min-h-16 items-center gap-3 px-4 py-3 after:absolute after:bottom-0 after:left-16 after:right-0 after:h-px after:bg-line-subtle after:content-[''] last:after:hidden"
    >
      <span
        aria-hidden="true"
        className={`flex size-[34px] shrink-0 items-center justify-center rounded-[10px] ${tile.className}`}
      >
        {tile.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body font-semibold text-fg">
          {operation}
        </span>
        {recordId !== undefined && (
          <span className="block truncate text-caption text-fg-subtle">
            {recordId}
          </span>
        )}
      </span>
    </div>
  );
}

export function ProposalReview({
  proposal,
  onAccept,
  onReject,
}: {
  proposal: ProposalSummary;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const act = async (fn: () => Promise<void>) => {
    // Repeated actions are blocked while applying: every operation lands once,
    // in one transaction.
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (cause: unknown) {
      setActionError(
        cause instanceof Error
          ? cause.message
          : "Command could not be completed.",
      );
    } finally {
      setBusy(false);
    }
  };
  const status = STATUS[proposal.status];
  const pending = proposal.status === "pending";
  return (
    <>
      <section
        aria-label={t("proposal.title")}
        className="flex flex-col gap-4 pb-4"
      >
        {actionError && pending && <Banner tone="error">{actionError}</Banner>}
        <Card>
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent-subtle text-accent-fg"
            >
              <Icons.Sparkles size={22} />
            </span>
            <div className="min-w-0 flex-1">
              {pending ? (
                <p className="text-body text-fg">{t("proposal.summary")}</p>
              ) : (
                <Badge tone={status?.tone ?? "neutral"}>
                  {status ? t(status.key) : proposal.status}
                </Badge>
              )}
            </div>
          </div>
        </Card>
        {proposal.changes.length > 0 && (
          <div className="flex flex-col gap-2">
            <GroupLabel className="px-2">{t("proposal.changes")}</GroupLabel>
            <Card flush>
              <ul>
                {proposal.changes.map((change, index) => (
                  <li key={`${proposal.id}:${String(index)}`}>
                    <ChangeRow change={change} />
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </section>
      {pending && (
        <ActionBar className="-mx-gutter mt-auto">
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => {
              void act(onReject);
            }}
          >
            {t("proposal.reject")}
          </Button>
          <Button
            loading={busy}
            onClick={() => {
              void act(onAccept);
            }}
          >
            <Icons.Check size={18} />
            {t("proposal.apply")}
          </Button>
        </ActionBar>
      )}
    </>
  );
}
